import { AmountInfoBoxLocalized, Translate } from '@components'

import { AmountInfoBoxesGroup, AmountInfoBoxProps, Icon, Space, Text, Tooltip } from '@billy/design-system'
import { ReactElement, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { billStatusToBadgeColor } from '@features/bills/constants/billStatusToBadgeVariant'
import { billStatusToTitleKey } from '@features/bills/constants/billStatusToTitleKey'
import { BillStatus } from '@features/bills/enums/billStatus'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { useBillsListFilters } from '../../hooks/useBillsListFilters'
import { useBillsSummaryFiltered } from '../../hooks/useBillsSummaryFiltered'
import * as Styled from './styles'

const SUMMARY_BOXES_STATUSES: BillStatus[] = [
  BillStatus.All,
  BillStatus.Draft,
  BillStatus.Overdue,
  BillStatus.Unpaid,
  BillStatus.Paid,
]

type SummaryInfoBox = Pick<
  AmountInfoBoxProps<BillStatus>,
  'active' | 'amount' | 'badgeAmount' | 'badgeLabel' | 'badgeColor' | 'id'
>

export const BillsSummary = (): ReactElement => {
  const { t } = useTranslation()
  const { organization } = useUserOrganization()
  const { data, isLoading } = useBillsSummaryFiltered()
  const [{ status: currentStatus }, setFilters] = useBillsListFilters()

  const handleClick = useCallback(
    (id: BillStatus) => {
      setFilters({ page: 1, status: id })
    },
    [setFilters],
  )

  const infoBoxes: SummaryInfoBox[] = useMemo(
    () =>
      SUMMARY_BOXES_STATUSES.map((status) => {
        const { amount: dataAmount, count: dataCount } = data?.[status] || {}
        const badgeAmount = dataCount
        const amount = dataAmount

        return {
          active: status === currentStatus,
          amount,
          badgeAmount,
          badgeLabel: t(billStatusToTitleKey[status], { count: badgeAmount }),
          badgeColor: billStatusToBadgeColor[status],
          id: status,
        }
      }),
    [currentStatus, data, t],
  )

  return (
    <Styled.BillsSummaryWrapper>
      <Styled.BillsSummaryTitleWrapper>
        <Text variant="h4">
          <Translate value="bills.summary.title" />
        </Text>
        <Styled.BillsSummaryTooltipWrapper>
          <Tooltip label={t('bills.summary.title.tooltip')} placement="right">
            <Icon icon="info" size="md" />
          </Tooltip>
        </Styled.BillsSummaryTooltipWrapper>
      </Styled.BillsSummaryTitleWrapper>
      <Space size="s16" />
      <AmountInfoBoxesGroup itemsInRow={infoBoxes.length} loading={isLoading}>
        {infoBoxes.map(({ active, amount, badgeAmount, badgeLabel, badgeColor, id }) => (
          <AmountInfoBoxLocalized
            active={active}
            amount={amount}
            badgeAmount={badgeAmount}
            badgeLabel={badgeLabel}
            badgeColor={badgeColor}
            clickable
            currencyId={organization?.baseCurrencyId}
            id={id}
            key={id}
            onClick={handleClick}
          />
        ))}
      </AmountInfoBoxesGroup>
    </Styled.BillsSummaryWrapper>
  )
}
